import {
  DOCUMENT_MAX_BYTES,
  type Client,
  type CopyPromptsResponse,
  type InstantlyConnection,
  type InstantlyConnectionSecretResponse,
  type OnboardingChecklist,
  type RegisterWebhookResponse,
  type Tenant,
  type TenantFlagsResponse,
} from '@platform/contracts';
import { Badge, Button, Callout, Card, CardContent, Switch, cn } from '@platform/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Circle, Copy, Info } from 'lucide-react';
import { type ReactNode, type SubmitEvent, useState } from 'react';
import { toast } from 'sonner';

import { Field, FormError, fd } from '../components/form';
import { api, errorMessage } from '../lib/api';
import { fmtBytes } from '../lib/console';
import { fileToBase64 } from '../lib/format';
import { meQuery } from '../lib/queries';
import { NativeSelect } from './superadmin';

/**
 * Wizard "Nueva empresa" (F4a, superadmin): cliente → empresa → Instantly → Gmail/dossier →
 * prompts → personas → funciones → checklist. Cada paso usa los endpoints de siempre; los pasos
 * después de crear la empresa se pueden saltar y retomar (el checklist dice qué falta).
 */

export const WIZARD_STEPS = [
  'Cliente',
  'Empresa',
  'Instantly',
  'Gmail y dossier',
  'Prompts',
  'Personas',
  'Funciones',
  'Checklist',
] as const;

interface WizardState {
  client: { id: string; name: string } | null;
  tenant: { id: string; slug: string; name: string } | null;
}

/** Ejecuta un submit con estado de "ocupado" y error visible; devuelve true si salió bien. */
function useStepAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function StepFooter({
  busy,
  onSkip,
  submitLabel = 'Guardar y seguir',
}: {
  busy: boolean;
  onSkip?: () => void;
  submitLabel?: string;
}) {
  return (
    <div className="flex flex-wrap gap-2 pt-2">
      <Button type="submit" disabled={busy}>
        {submitLabel}
      </Button>
      {onSkip ? (
        <Button type="button" variant="ghost" onClick={onSkip} disabled={busy}>
          Saltar este paso
        </Button>
      ) : null}
    </div>
  );
}

function CopyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{value}</code>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={`Copiar ${label}`}
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => toast.success('Copiado'));
          }}
        >
          <Copy />
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ pasos

function ClientStep({ onDone }: { onDone: (c: { id: string; name: string }) => void }) {
  const clients = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => api<Client[]>('/api/admin/clients'),
  });
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const { busy, error, run } = useStepAction();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      if (mode === 'existing') {
        const c = clients.data?.find((x) => x.id === fd(f, 'clientId'));
        if (!c) throw new Error('Elige un cliente');
        onDone({ id: c.id, name: c.name });
        return;
      }
      const c = await api<Client>('/api/admin/clients', {
        method: 'POST',
        body: { name: fd(f, 'name'), slug: fd(f, 'slug') },
      });
      toast.success('Cliente creado');
      onDone({ id: c.id, name: c.name });
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex gap-4 text-sm" role="radiogroup" aria-label="Cliente">
        {(['existing', 'new'] as const).map((m) => (
          <label key={m} className="flex items-center gap-2">
            <input
              type="radio"
              name="mode"
              checked={mode === m}
              onChange={() => {
                setMode(m);
              }}
            />
            {m === 'existing' ? 'Cliente existente' : 'Cliente nuevo'}
          </label>
        ))}
      </div>
      {mode === 'existing' ? (
        <NativeSelect
          label="Cliente"
          name="clientId"
          options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
      ) : (
        <>
          <Field label="Nombre del cliente" name="name" required />
          <Field
            label="Slug del cliente"
            name="slug"
            pattern="[a-z0-9][a-z0-9-]*"
            hint="Minúsculas, números y guiones"
            required
          />
        </>
      )}
      <FormError message={error} />
      <StepFooter busy={busy} submitLabel="Seguir" />
    </form>
  );
}

function TenantStep({
  client,
  onDone,
}: {
  client: { id: string; name: string };
  onDone: (t: { id: string; slug: string; name: string }) => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useStepAction();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      const t = await api<Tenant>('/api/admin/tenants', {
        method: 'POST',
        body: {
          clientId: client.id,
          name: fd(f, 'name'),
          slug: fd(f, 'slug'),
          timezone: fd(f, 'timezone') || 'Europe/Madrid',
        },
      });
      toast.success('Empresa creada (estado: onboarding)');
      await qc.invalidateQueries({ queryKey: meQuery.queryKey });
      onDone({ id: t.id, slug: t.slug, name: t.name });
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Cliente: <strong>{client.name}</strong>. La empresa arranca en estado <em>onboarding</em>.
      </p>
      <Field label="Nombre de la empresa" name="name" required />
      <Field label="Slug" name="slug" pattern="[a-z0-9][a-z0-9-]*" required />
      <Field label="Zona horaria" name="timezone" defaultValue="Europe/Madrid" required />
      <FormError message={error} />
      <StepFooter busy={busy} submitLabel="Crear empresa" />
    </form>
  );
}

/**
 * "Todas las empresas": cada tenant devuelve su propia lista de conexiones, y desde el cambio que
 * suma las conexiones COMPARTIDAS de otra empresa (la que ya usa vía una campaña), la misma
 * conexión aparece repetida una vez por cada empresa que la ve. Deduplica por id, priorizando la
 * vista de la dueña (`isOwner: true`, con los datos completos) sobre la redactada de una invitada.
 */
export function dedupeSharedConnections(
  lists: readonly InstantlyConnection[][],
): InstantlyConnection[] {
  const byId = new Map<string, InstantlyConnection>();
  for (const c of lists.flat()) {
    if (!c.isShared) continue;
    const existing = byId.get(c.id);
    if (!existing || (!existing.isOwner && c.isOwner)) byId.set(c.id, c);
  }
  return [...byId.values()];
}

function InstantlyStep({
  tenant,
  onNext,
}: {
  tenant: { id: string; slug: string };
  onNext: () => void;
}) {
  const [mode, setMode] = useState<'shared' | 'new'>('new');
  const [created, setCreated] = useState<InstantlyConnectionSecretResponse | null>(null);
  const [hook, setHook] = useState<RegisterWebhookResponse | null>(null);
  const { busy, error, run } = useStepAction();
  const shared = useQuery({
    queryKey: ['admin', 'shared-connections'],
    queryFn: async () => {
      const ts = await api<Tenant[]>('/api/admin/tenants');
      const lists = await Promise.all(
        ts.map((t) =>
          api<InstantlyConnection[]>(`/api/tenants/${t.id}/instantly-connections`).catch(
            () => [] as InstantlyConnection[],
          ),
        ),
      );
      return dedupeSharedConnections(lists);
    },
    enabled: mode === 'shared',
  });

  const createConnection = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      const res = await api<InstantlyConnectionSecretResponse>(
        `/api/tenants/${tenant.id}/instantly-connections`,
        {
          method: 'POST',
          body: {
            label: fd(f, 'label'),
            ...(fd(f, 'apiKey') ? { apiKey: fd(f, 'apiKey') } : {}),
            ...(fd(f, 'workspaceId') ? { workspaceId: fd(f, 'workspaceId') } : {}),
          },
        },
      );
      if (fd(f, 'campaignId')) {
        await api(
          `/api/tenants/${tenant.id}/instantly-connections/${res.connection.id}/campaigns`,
          {
            method: 'POST',
            body: { externalId: fd(f, 'campaignId'), name: fd(f, 'campaignName') || 'Campaña' },
          },
        );
      }
      setCreated(res);
      toast.success('Conexión creada');
    });
  };

  const mapShared = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      const conn = shared.data?.find((c) => c.id === fd(f, 'connectionId'));
      if (!conn) throw new Error('Elige una conexión compartida');
      await api(`/api/tenants/${conn.tenantId}/instantly-connections/${conn.id}/campaigns`, {
        method: 'POST',
        body: {
          externalId: fd(f, 'campaignId'),
          name: fd(f, 'campaignName') || 'Campaña',
          tenantId: tenant.id,
        },
      });
      toast.success('Campaña mapeada en la conexión compartida');
      onNext();
    });
  };

  const register = () => {
    if (!created) return;
    void run(async () => {
      const r = await api<RegisterWebhookResponse>(
        `/api/tenants/${tenant.id}/instantly-connections/${created.connection.id}/register-webhook`,
        { method: 'POST' },
      );
      setHook(r);
      toast.success(r.action === 'created' ? 'Webhook registrado' : 'Webhook actualizado');
    });
  };

  if (created) {
    return (
      <div className="flex flex-col gap-4">
        <Callout tone="warning" icon={<Info />} title="El secreto se muestra una sola vez">
          Si registras el webhook a mano en Instantly: <code>all_events</code>, sin campaña, y el
          secreto en el header <code>X-Ingest-Token</code>. Con el botón lo hace la plataforma (y
          genera un secreto nuevo).
        </Callout>
        <CopyValue label="URL del webhook" value={created.webhookUrl} />
        <CopyValue label="Secreto (X-Ingest-Token)" value={created.webhookSecret} />
        {hook ? (
          <p className="text-sm">
            <Badge variant="outline">{hook.action === 'created' ? 'creado' : 'actualizado'}</Badge>{' '}
            Webhook <code>{hook.webhookId}</code> registrado en Instantly.
          </p>
        ) : null}
        <FormError message={error} />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={register}
            disabled={busy || !created.connection.hasApiKey || hook !== null}
          >
            Registrar webhook en Instantly
          </Button>
          <Button type="button" variant="outline" onClick={onNext}>
            Seguir
          </Button>
        </div>
        {!created.connection.hasApiKey ? (
          <p className="text-xs text-muted-foreground">
            Sin API key no se puede registrar automáticamente.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 text-sm" role="radiogroup" aria-label="Conexión de Instantly">
        {(['new', 'shared'] as const).map((m) => (
          <label key={m} className="flex items-center gap-2">
            <input
              type="radio"
              name="instantly-mode"
              checked={mode === m}
              onChange={() => {
                setMode(m);
              }}
            />
            {m === 'new' ? 'Workspace propio (recomendado)' : 'Campaña en una conexión compartida'}
          </label>
        ))}
      </div>
      {mode === 'new' ? (
        <form onSubmit={createConnection} className="flex flex-col gap-4">
          <Field
            label="Nombre de la conexión"
            name="label"
            defaultValue={`Workspace ${tenant.slug}`}
            required
          />
          <Field label="API key de Instantly" name="apiKey" type="password" autoComplete="off" />
          <Field label="Workspace ID (opcional)" name="workspaceId" />
          <Field label="ID de la campaña (opcional)" name="campaignId" />
          <Field label="Nombre de la campaña" name="campaignName" />
          <FormError message={error} />
          <StepFooter busy={busy} onSkip={onNext} submitLabel="Crear conexión" />
        </form>
      ) : (
        <form onSubmit={mapShared} className="flex flex-col gap-4">
          <NativeSelect
            label="Conexión compartida"
            name="connectionId"
            options={(shared.data ?? []).map((c) => ({ value: c.id, label: c.label }))}
          />
          <Field label="ID de la campaña" name="campaignId" required />
          <Field label="Nombre de la campaña" name="campaignName" required />
          <FormError message={error} />
          <StepFooter busy={busy} onSkip={onNext} submitLabel="Mapear campaña" />
        </form>
      )}
    </div>
  );
}

function GmailDossierStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }) {
  const { busy, error, run } = useStepAction();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      const sender = fd(f, 'sender');
      if (sender) {
        await api(`/api/tenants/${tenantId}/integrations`, {
          method: 'POST',
          body: { kind: 'gmail', label: 'Gmail remitente', config: { sender } },
        });
      }
      const file = f.get('file');
      if (file instanceof File && file.size > 0) {
        if (file.size > DOCUMENT_MAX_BYTES) {
          throw new Error(`El archivo supera ${fmtBytes(DOCUMENT_MAX_BYTES)}`);
        }
        await api(`/api/tenants/${tenantId}/documents/dossier/versions`, {
          method: 'POST',
          body: {
            filename: file.name,
            mime: file.type || 'application/pdf',
            contentBase64: await fileToBase64(file),
            note: 'Alta de la empresa (wizard)',
          },
        });
      }
      toast.success('Guardado');
      onNext();
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Sólo si el bot contesta por Gmail (primer contacto con dossier). El envío sigue saliendo del
        n8n de la empresa con la delegación del Service Account.
      </p>
      <Field label="Remitente (email de Gmail)" name="sender" type="email" />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="wizard-dossier" className="text-sm font-medium">
          Dossier (PDF)
        </label>
        <input id="wizard-dossier" name="file" type="file" accept="application/pdf" />
      </div>
      <FormError message={error} />
      <StepFooter busy={busy} onSkip={onNext} />
    </form>
  );
}

function PromptsStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }) {
  const tenants = useQuery({
    queryKey: ['admin', 'tenants', ''],
    queryFn: () => api<Tenant[]>('/api/admin/tenants'),
  });
  const [result, setResult] = useState<CopyPromptsResponse | null>(null);
  const { busy, error, run } = useStepAction();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void run(async () => {
      const r = await api<CopyPromptsResponse>(`/api/tenants/${tenantId}/prompts/copy-from`, {
        method: 'POST',
        body: { sourceTenantId: fd(f, 'source') },
      });
      setResult(r);
      toast.success(`${String(r.copied.length)} prompt(s) copiados como borrador`);
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Se copian como <strong>borradores</strong>: revisalos y publicalos desde Prompts cuando
        estén adaptados a la empresa.
      </p>
      <NativeSelect
        label="Copiar de"
        name="source"
        options={(tenants.data ?? [])
          .filter((t) => t.id !== tenantId)
          .map((t) => ({ value: t.id, label: t.name }))}
      />
      {result ? (
        <ul className="text-sm">
          {result.copied.map((c) => (
            <li key={c.name}>
              {c.name}: borrador v{c.draftVersion} (desde{' '}
              {c.origin === 'legacy' ? 'el stack' : `v${String(c.fromVersion)}`})
            </li>
          ))}
          {result.skipped.length ? <li>Sin origen: {result.skipped.join(', ')}</li> : null}
        </ul>
      ) : null}
      <FormError message={error} />
      <div className="flex flex-wrap gap-2 pt-2">
        <Button type="submit" disabled={busy}>
          Copiar prompts
        </Button>
        <Button type="button" variant={result ? 'default' : 'ghost'} onClick={onNext}>
          {result ? 'Seguir' : 'Saltar este paso'}
        </Button>
      </div>
    </form>
  );
}

function PeopleStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }) {
  const [sent, setSent] = useState<string[]>([]);
  const { busy, error, run } = useStepAction();
  const submit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    void run(async () => {
      const email = fd(f, 'email');
      await api('/api/invitations', {
        method: 'POST',
        body: { email, scopeType: 'tenant', scopeId: tenantId, role: fd(f, 'role') },
      });
      setSent((s) => [...s, email]);
      form.reset();
      toast.success(`Invitación enviada a ${email}`);
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <Field label="Email" name="email" type="email" required />
      <NativeSelect
        label="Rol"
        name="role"
        options={[
          { value: 'client_admin', label: 'Admin del cliente' },
          { value: 'client_viewer', label: 'Lectura del cliente' },
          { value: 'operator', label: 'Operador (agencia)' },
          { value: 'viewer', label: 'Lectura (agencia)' },
        ]}
      />
      {sent.length ? <p className="text-sm">Invitados: {sent.join(', ')}</p> : null}
      <FormError message={error} />
      <div className="flex flex-wrap gap-2 pt-2">
        <Button type="submit" disabled={busy}>
          Invitar
        </Button>
        <Button type="button" variant={sent.length ? 'default' : 'ghost'} onClick={onNext}>
          {sent.length ? 'Seguir' : 'Saltar este paso'}
        </Button>
      </div>
    </form>
  );
}

function FlagsStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }) {
  const q = useQuery({
    queryKey: ['flags', tenantId],
    queryFn: () => api<TenantFlagsResponse>(`/api/tenants/${tenantId}/flags`),
  });
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const { busy, error, run } = useStepAction();
  const save = () => {
    void run(async () => {
      if (Object.keys(draft).length > 0) {
        await api(`/api/tenants/${tenantId}/flags`, { method: 'PUT', body: { flags: draft } });
        toast.success('Funciones guardadas');
      }
      onNext();
    });
  };
  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-3">
        {(q.data?.flags ?? []).map((f) => (
          <li key={f.key} className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{f.label}</p>
              <p className="text-xs text-muted-foreground">{f.description}</p>
            </div>
            <Switch
              aria-label={f.label}
              checked={draft[f.key] ?? f.enabled}
              onCheckedChange={(v) => {
                setDraft((d) => ({ ...d, [f.key]: v }));
              }}
            />
          </li>
        ))}
      </ul>
      <FormError message={error} />
      <div className="flex gap-2">
        <Button type="button" onClick={save} disabled={busy}>
          Guardar y ver checklist
        </Button>
      </div>
    </div>
  );
}

export function OnboardingChecklistView({ tenantId }: { tenantId: string }) {
  const q = useQuery({
    queryKey: ['onboarding', tenantId],
    queryFn: () => api<OnboardingChecklist>(`/api/tenants/${tenantId}/onboarding`),
  });
  if (q.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (q.error) return <FormError message={errorMessage(q.error)} />;
  const c = q.data;
  if (!c) return null;
  return (
    <div className="flex flex-col gap-6">
      <Callout
        tone={c.platformReady ? 'success' : 'warning'}
        icon={<Info />}
        title={c.platformReady ? 'La plataforma está lista' : 'Faltan pasos en la plataforma'}
      >
        {c.name} ({c.slug}) · estado {c.status}. Lo del motor (n8n) se hace aparte hasta la
        consolidación.
      </Callout>
      <section aria-label="Plataforma">
        <h3 className="mb-2 text-sm font-semibold">Plataforma</h3>
        <ul className="flex flex-col gap-2">
          {c.platform.map((s) => (
            <li key={s.key} className="flex items-start gap-2 text-sm">
              {s.done ? (
                <Check className="mt-0.5 size-4 text-success" aria-label="hecho" />
              ) : (
                <Circle className="mt-0.5 size-4 text-muted-foreground" aria-label="pendiente" />
              )}
              <div>
                <span className={cn(s.done ? '' : 'font-medium')}>{s.label}</span>
                {s.optional ? (
                  <span className="text-xs text-muted-foreground"> (opcional)</span>
                ) : null}
                {s.detail ? <p className="text-xs text-muted-foreground">{s.detail}</p> : null}
              </div>
            </li>
          ))}
        </ul>
      </section>
      <section aria-label="Motor (n8n)">
        <h3 className="mb-2 text-sm font-semibold">Motor (n8n) — pasos manuales</h3>
        <ol className="flex list-decimal flex-col gap-3 pl-5">
          {c.engine.map((s) => (
            <li key={s.key} className="text-sm">
              <p className="font-medium">{s.label}</p>
              <p className="text-xs text-muted-foreground">{s.detail}</p>
              {s.command ? (
                <code className="mt-1 block overflow-x-auto rounded bg-muted px-2 py-1 text-xs">
                  {s.command}
                </code>
              ) : null}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Stepper({ current }: { current: number }) {
  return (
    <ol aria-label="Pasos" className="mb-6 flex flex-wrap gap-2 text-xs">
      {WIZARD_STEPS.map((label, i) => (
        <li
          key={label}
          aria-current={i === current ? 'step' : undefined}
          className={cn(
            'rounded-full border px-3 py-1',
            i === current
              ? 'border-primary bg-primary/10 font-medium text-foreground'
              : i < current
                ? 'border-transparent bg-muted text-muted-foreground'
                : 'text-muted-foreground',
          )}
        >
          {String(i + 1)}. {label}
        </li>
      ))}
    </ol>
  );
}

export function OnboardingWizard({ initialTenantId }: { initialTenantId?: string }) {
  const [step, setStep] = useState(initialTenantId ? WIZARD_STEPS.length - 1 : 0);
  const [state, setState] = useState<WizardState>({ client: null, tenant: null });
  const tenantId = state.tenant?.id ?? initialTenantId ?? null;
  const next = () => {
    setStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
  };
  let body: ReactNode = null;
  if (step === 0) {
    body = (
      <ClientStep
        onDone={(client) => {
          setState({ client, tenant: null });
          next();
        }}
      />
    );
  } else if (step === 1 && state.client) {
    body = (
      <TenantStep
        client={state.client}
        onDone={(tenant) => {
          setState((s) => ({ ...s, tenant }));
          next();
        }}
      />
    );
  } else if (tenantId && state.tenant && step === 2) {
    body = <InstantlyStep tenant={state.tenant} onNext={next} />;
  } else if (tenantId && step === 3) {
    body = <GmailDossierStep tenantId={tenantId} onNext={next} />;
  } else if (tenantId && step === 4) {
    body = <PromptsStep tenantId={tenantId} onNext={next} />;
  } else if (tenantId && step === 5) {
    body = <PeopleStep tenantId={tenantId} onNext={next} />;
  } else if (tenantId && step === 6) {
    body = <FlagsStep tenantId={tenantId} onNext={next} />;
  } else if (tenantId) {
    body = <OnboardingChecklistView tenantId={tenantId} />;
  }
  return (
    <div className="flex flex-col">
      <Stepper current={step} />
      <Card>
        <CardContent className="flex flex-col gap-4 p-6">
          <h2 className="text-base font-semibold">{WIZARD_STEPS[step]}</h2>
          {body}
        </CardContent>
      </Card>
    </div>
  );
}

/** Sección del superadmin: `?tenant=<id>` abre directo el checklist de una empresa existente. */
export function OnboardingSection() {
  const tenant =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('tenant');
  return <OnboardingWizard {...(tenant ? { initialTenantId: tenant } : {})} />;
}
